import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Float, MeshTransmissionMaterial, ContactShadows } from "@react-three/drei";
import * as THREE from "three";

/**
 * "Aegis" — a sculptural composition designed for editorial-grade impact.
 *  ▸ A polished chrome capsule at the centre (an abstract medical artefact)
 *  ▸ Three elegant orbital rings in champagne / blue tinted metals
 *  ▸ A glass medical-cross hovering inside the inner ring
 *  ▸ A glowing core orb providing back-light
 *  ▸ Floating micro crosses + particles for atmosphere
 *  ▸ Camera parallax driven by mouse position
 */

const useMouseParallax = (ref, strength = 0.4) => {
    useFrame((state) => {
        if (!ref.current) return;
        const { pointer } = state;
        ref.current.rotation.y = THREE.MathUtils.lerp(
            ref.current.rotation.y,
            pointer.x * strength,
            0.04
        );
        ref.current.rotation.x = THREE.MathUtils.lerp(
            ref.current.rotation.x,
            -pointer.y * strength * 0.55,
            0.04
        );
    });
};

const ChromeCapsule = () => {
    const ref = useRef();
    useFrame((state, delta) => {
        if (ref.current) {
            ref.current.rotation.y += delta * 0.18;
            ref.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.4) * 0.04;
        }
    });
    return (
        <group ref={ref}>
            {/* Capsule body */}
            <mesh>
                <capsuleGeometry args={[0.42, 1.5, 24, 64]} />
                <meshStandardMaterial
                    color="#E8EEF6"
                    metalness={1}
                    roughness={0.08}
                    envMapIntensity={1.6}
                />
            </mesh>
            {/* Inset waist band — bisleri blue */}
            <mesh>
                <torusGeometry args={[0.43, 0.03, 16, 96]} />
                <meshStandardMaterial color="#0E4D92" metalness={1} roughness={0.2} envMapIntensity={1.4} />
            </mesh>
            <mesh position={[0, 0.18, 0]}>
                <torusGeometry args={[0.43, 0.012, 16, 96]} />
                <meshStandardMaterial color="#4CAF50" metalness={1} roughness={0.25} />
            </mesh>
            <mesh position={[0, -0.18, 0]}>
                <torusGeometry args={[0.43, 0.012, 16, 96]} />
                <meshStandardMaterial color="#4CAF50" metalness={1} roughness={0.25} />
            </mesh>
        </group>
    );
};

const OrbitalRing = ({ radius = 1.4, tube = 0.025, color = "#C3A37A", tilt = [0.7, 0.3, 0], speed = 0.4 }) => {
    const ref = useRef();
    useFrame((_, delta) => {
        if (ref.current) {
            ref.current.rotation.z += delta * speed;
        }
    });
    return (
        <group rotation={tilt}>
            <mesh ref={ref}>
                <torusGeometry args={[radius, tube, 24, 200]} />
                <meshStandardMaterial color={color} metalness={1} roughness={0.18} envMapIntensity={1.6} />
            </mesh>
            {/* Orbiting sphere on the ring */}
            <mesh position={[radius, 0, 0]}>
                <sphereGeometry args={[tube * 2.2, 16, 16]} />
                <meshStandardMaterial color={color} metalness={1} roughness={0.15} />
            </mesh>
        </group>
    );
};

const GlowCore = () => {
    const ref = useRef();
    useFrame((state) => {
        if (ref.current) {
            const t = state.clock.elapsedTime;
            const s = 1 + Math.sin(t * 1.8) * 0.06;
            ref.current.scale.set(s, s, s);
        }
    });
    return (
        <mesh ref={ref} position={[0, 0, -0.6]}>
            <sphereGeometry args={[0.65, 64, 64]} />
            <meshBasicMaterial color="#1158CC" transparent opacity={0.18} />
        </mesh>
    );
};

const GlassCross = ({ position = [0, 0, 0.6] }) => {
    const ref = useRef();
    useFrame((state, delta) => {
        if (ref.current) {
            ref.current.rotation.y += delta * 0.6;
            ref.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.8) * 0.15;
        }
    });
    return (
        <Float speed={1.4} rotationIntensity={0.3} floatIntensity={0.8}>
            <group ref={ref} position={position} scale={0.4}>
                <mesh>
                    <boxGeometry args={[1.0, 0.32, 0.32]} />
                    <MeshTransmissionMaterial
                        backside
                        samples={4}
                        thickness={0.3}
                        chromaticAberration={0.05}
                        anisotropy={0.4}
                        distortion={0.1}
                        transmission={1}
                        roughness={0.05}
                        color="#DCEAFB"
                    />
                </mesh>
                <mesh>
                    <boxGeometry args={[0.32, 1.0, 0.32]} />
                    <MeshTransmissionMaterial
                        backside
                        samples={4}
                        thickness={0.3}
                        chromaticAberration={0.05}
                        anisotropy={0.4}
                        distortion={0.1}
                        transmission={1}
                        roughness={0.05}
                        color="#DCEAFB"
                    />
                </mesh>
            </group>
        </Float>
    );
};

const FloatingMicroCross = ({ position, scale = 0.3, color = "#0E4D92", speed = 0.4 }) => {
    const ref = useRef();
    useFrame((state, delta) => {
        if (ref.current) {
            ref.current.rotation.y += delta * speed;
            ref.current.rotation.x = Math.sin(state.clock.elapsedTime * speed) * 0.4;
        }
    });
    return (
        <Float speed={1.6} rotationIntensity={0.4} floatIntensity={1.2}>
            <group ref={ref} position={position} scale={scale}>
                <mesh>
                    <boxGeometry args={[0.7, 0.2, 0.2]} />
                    <meshStandardMaterial color={color} metalness={0.95} roughness={0.18} envMapIntensity={1.5} />
                </mesh>
                <mesh>
                    <boxGeometry args={[0.2, 0.7, 0.2]} />
                    <meshStandardMaterial color={color} metalness={0.95} roughness={0.18} envMapIntensity={1.5} />
                </mesh>
            </group>
        </Float>
    );
};

const Particles = ({ mobile = false }) => {
    const ref = useRef();
    const count = mobile ? 50 : 160;
    const positions = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            arr[i * 3] = (Math.random() - 0.5) * 14;
            arr[i * 3 + 1] = (Math.random() - 0.5) * 9;
            arr[i * 3 + 2] = (Math.random() - 0.5) * 8 - 2;
        }
        return arr;
    }, [count]);
    useFrame((state) => {
        if (ref.current) {
            ref.current.rotation.y = state.clock.elapsedTime * 0.04;
        }
    });
    return (
        <points ref={ref}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial size={0.025} color="#0E4D92" sizeAttenuation transparent opacity={0.32} />
        </points>
    );
};

const Composition = ({ mobile }) => {
    const groupRef = useRef();
    useMouseParallax(groupRef, mobile ? 0.18 : 0.4);

    useFrame((state, delta) => {
        if (groupRef.current) {
            // gentle base float
            groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.6) * 0.05;
        }
    });

    return (
        <group ref={groupRef} position={mobile ? [0, -0.2, 0] : [3.6, 0, 0]} scale={mobile ? 0.8 : 1}>
            <GlowCore />
            <ChromeCapsule />
            <GlassCross position={[0, 0, 0.95]} />

            {/* Three orbital rings at varied tilts/colours/speeds */}
            <OrbitalRing radius={1.15} tube={0.022} color="#C3A37A" tilt={[0.7, 0.3, 0]} speed={0.5} />
            <OrbitalRing radius={1.45} tube={0.018} color="#0E4D92" tilt={[-0.4, 0.8, 0.3]} speed={-0.35} />
            <OrbitalRing radius={1.78} tube={0.014} color="#4CAF50" tilt={[1.1, -0.4, 0.2]} speed={0.3} />
        </group>
    );
};

const SceneCamera = ({ mobile }) => {
    const { camera } = useThree();
    useFrame((state) => {
        camera.position.x = THREE.MathUtils.lerp(camera.position.x, state.pointer.x * 0.3, 0.05);
        camera.position.y = THREE.MathUtils.lerp(camera.position.y, state.pointer.y * 0.18, 0.05);
        camera.lookAt(0, 0, 0);
    });
    return null;
};

const HeroScene = ({ mobile = false }) => {
    return (
        <Canvas
            camera={{ position: [0, 0, mobile ? 6.5 : 5.5], fov: mobile ? 44 : 38 }}
            dpr={mobile ? [1, 1.5] : [1, 2]}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            data-testid="hero-3d-canvas"
        >
            <Suspense fallback={null}>
                <ambientLight intensity={0.7} />
                <directionalLight position={[5, 5, 5]} intensity={1.3} color="#FFFFFF" />
                <directionalLight position={[-4, -2, -1]} intensity={0.55} color="#C7DBF1" />
                <pointLight position={[2, 2, 3]} intensity={0.9} color="#1158CC" />
                <pointLight position={[-2, -2, 3]} intensity={0.6} color="#C3A37A" />
                <spotLight position={[0, 6, 4]} intensity={0.6} angle={0.4} penumbra={0.8} color="#FFFFFF" />

                <Composition mobile={mobile} />

                {!mobile && (
                    <>
                        <FloatingMicroCross position={[-3.0, 1.9, -1]} scale={0.55} color="#0E4D92" />
                        <FloatingMicroCross position={[-3.4, -1.6, -1.2]} scale={0.4} color="#4CAF50" speed={-0.5} />
                        <FloatingMicroCross position={[-2.6, 0.5, -2.1]} scale={0.28} color="#C3A37A" speed={0.7} />
                        <FloatingMicroCross position={[5.6, 2.0, -2.5]} scale={0.4} color="#1158CC" />
                        <FloatingMicroCross position={[5.4, -1.8, -2.1]} scale={0.32} color="#C3A37A" speed={-0.6} />
                    </>
                )}

                <Particles mobile={mobile} />

                {!mobile && (
                    <ContactShadows
                        position={[3.6, -2.2, 0]}
                        opacity={0.18}
                        scale={6}
                        blur={2.4}
                        far={3}
                        color="#0E4D92"
                    />
                )}

                <Environment preset="studio" environmentIntensity={0.55} />
                <SceneCamera mobile={mobile} />
            </Suspense>
        </Canvas>
    );
};

export default HeroScene;
